import React, { JSX, Suspense, useMemo, useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, useGLTF, Environment, PerspectiveCamera } from '@react-three/drei';
import Box from '@mui/material/Box';
import { Book } from '../types/book';

// Props for the Model component
interface ModelProps {
  modelPath: string;
  rotationX?: number;
  rotationY?: number;
  rotationZ?: number;
  containerWidth?: number;
  containerHeight?: number;
}

// Props for the Novel3DViewer component
interface Novel3DViewerProps {
  modelPath: string;
  book: Book;
  title: string;
  description: string;
  initialAzimuthalAngleDegrees?: number;
  initialPolarAngleDegrees?: number;
  distanceFromTarget?: number;
  modelRotationX?: number;
  modelRotationY?: number;
  modelRotationZ?: number;
  disabled?: boolean;
  maxWidth?: object | number;
  width?: object | number;
  height?: object | number;
}

// Model component with responsive scaling
function Model({
  modelPath,
  rotationX = 90,
  rotationY = 0,
  rotationZ = 0,
  containerWidth = 400,
  containerHeight = 400,
}: ModelProps): JSX.Element {
  const { scene } = useGLTF(modelPath);
  
  // Calculate responsive scale based on container size
  const scale = useMemo(() => {
    const baseScale = 1.5;
    const minDimension = Math.min(containerWidth, containerHeight);
    const scaleFactor = minDimension / 400; // 400px as base reference
    return baseScale * Math.max(0.8, Math.min(2, scaleFactor)); // Clamp between 0.8x and 2x
  }, [containerWidth, containerHeight]);
  
  const rotationRadians = [
    (rotationX * Math.PI) / 180,
    (rotationY * Math.PI) / 180,
    (rotationZ * Math.PI) / 180,
  ];
  
  return (
    <primitive
      object={scene}
      scale={scale}
      position={[0, -1, 0]}
      rotation={rotationRadians as [number, number, number]}
    />
  );
}

// Novel3DViewer component with improved responsive behavior
const Novel3DViewer: React.FC<Novel3DViewerProps> = ({
  modelPath,
  book,
  title,
  description,
  initialAzimuthalAngleDegrees = 45,
  initialPolarAngleDegrees = 70,
  distanceFromTarget = 2.7,
  modelRotationX = 90,
  modelRotationY = 0,
  modelRotationZ = 0,
  disabled = false,
  // Improved responsive sizing with consistent aspect ratios
  maxWidth = { xs: 280, sm: 360, md: 480, lg: 560, xl: 640 },
  width = { xs: 260, sm: 340, md: 460, lg: 540, xl: 620 },
  height = { xs: 260, sm: 340, md: 460, lg: 540, xl: 620}, // Match width for square aspect ratio
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerDimensions, setContainerDimensions] = useState({ width: 400, height: 400 });

  // Track container size changes
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerDimensions({ 
          width: rect.width, 
          height: rect.height 
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Target position
  const targetPosition = useMemo(() => new THREE.Vector3(0, -1, 0), []);

  // Convert angles to radians
  const theta = (initialAzimuthalAngleDegrees * Math.PI) / 180;
  const phi = (initialPolarAngleDegrees * Math.PI) / 180;
  const r = distanceFromTarget;

  // Calculate responsive camera settings
  const cameraSettings = useMemo(() => {
    const aspectRatio = containerDimensions.width / containerDimensions.height;
    
    // Adjust zoom based on container size and aspect ratio
    let zoom = 3.5;
    if (containerDimensions.width < 300) {
      zoom = 2.8; // Zoom out more for smaller screens
    } else if (containerDimensions.width < 400) {
      zoom = 3.1;
    }
    
    // Adjust distance for very wide or tall aspect ratios
    let adjustedDistance = r;
    if (aspectRatio > 1.5) {
      adjustedDistance = r * 1.2; // Move camera back for wide containers
    } else if (aspectRatio < 0.8) {
      adjustedDistance = r * 1.1; // Move camera back for tall containers
    }

    // Calculate camera position
    const x = targetPosition.x + adjustedDistance * Math.sin(theta) * Math.sin(phi);
    const y = targetPosition.y + adjustedDistance * Math.cos(phi);
    const z = targetPosition.z + adjustedDistance * Math.cos(theta) * Math.sin(phi);
    
    return {
      position: new THREE.Vector3(x, y, z),
      zoom,
      fov: aspectRatio < 1 ? 55 : 50, // Wider FOV for portrait orientations
    };
  }, [targetPosition, r, theta, phi, containerDimensions]);

  return (
    <Box 
      sx={{ 
        mb: 4,
        maxWidth: maxWidth,
        width: width,
      }}
    >
      <Box
        ref={containerRef}
        sx={{
          position: 'relative',
          width: '100%',
          height: height,
          borderRadius: '8px',
          overflow: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        <Canvas>
          <PerspectiveCamera
            makeDefault
            position={cameraSettings.position.toArray() as [number, number, number]}
            fov={cameraSettings.fov}
            zoom={cameraSettings.zoom}
          />
          <ambientLight intensity={2} />
          <spotLight position={[10, 10, 10]} angle={0.15} penumbra={1} />
          <pointLight position={[-10, -10, -10]} />

          <Suspense fallback={null}>
            <Model
              modelPath={modelPath}
              rotationX={modelRotationX}
              rotationY={modelRotationY}
              rotationZ={modelRotationZ}
              containerWidth={containerDimensions.width}
              containerHeight={containerDimensions.height}
            />
            <Environment preset="city" />
          </Suspense>

          <OrbitControls
            target={targetPosition.toArray() as [number, number, number]}
            enablePan={false}
            enableZoom={false}
            enableRotate={true}
          />
        </Canvas>
      </Box>
    </Box>
  );
};

export default Novel3DViewer;
